import type { SearchParameterName } from './derived';

/**
 * Every search parameter name, as values.
 *
 * The schema in ./data is still the one source — this is spelled out because
 * TypeScript cannot compute an enum from a type, and the assertions below fail
 * the build the moment the two disagree. Adding a row without adding a member
 * names the missing one in the error.
 *
 * Worth having rather than passing strings:
 * `searchParameters.get('orientations')` already rejects a typo, but it offers
 * no completion and nothing to find the callers of. Forty hand-rolled
 * `COLOR_INDEX_PARAM`-style constants across the app are the same idea,
 * declared forty times.
 */
export enum SearchParameterNameEnum {
  AGREEMENTS = 'agreements',
  FAMILY = 'family',
  ASSET_TYPE = 'assettype',
  MEDIA_TYPE = 'mediatype',
  ASSET_FILE_TYPE = 'assetfiletype',
  PHRASE = 'phrase',
  EVENTS = 'events',
  ARTIST_EXACT = 'artistexact',
  LOCATIONS = 'locations',
  SPECIFIC_PEOPLE = 'specificpeople',
  ORIENTATIONS = 'orientations',
  NUMBER_OF_PEOPLE = 'numberofpeople',
  AGE_OF_PEOPLE = 'ageofpeople',
  ETHNICITY = 'ethnicity',
  FILM_DEFINITION = 'filmdefinition',
  VIDEO_TEMPLATE_CATEGORY = 'videotemplatecategory',
  VIDEO_TEMPLATE_APP_VERSION = 'videotemplateappversion',
  MOOD = 'mood',
  SORT = 'sort',
  RECENCY = 'recency',
  PAGE = 'page',
  ALLOWED_USE = 'alloweduse',
  PHRASE_PROCESSING = 'phraseprocessing',
  OFFLINE_CONTENT = 'offlinecontent',
  COLOR_INDEX = '_colorindex',
  BEGIN_DATE = 'begindate',
  CLARIFICATIONS = 'clarifications',
  COLLECTIONS = 'collections',
  COLOR = 'color',
  COMPOSITIONS = 'compositions',
  CONTRIBUTOR = 'contributor',
  CURATED_SET = 'curatedset',
  EASY_ACCESS = 'easyaccess',
  EDITORIAL_PRODUCTS = 'editorialproducts',
  EDITORIAL_SOURCES = 'editorialsources',
  EDITORIAL_SUBSCRIPTION = 'editorialsubscription',
  EMBEDDABLE = 'embeddable',
  END_DATE = 'enddate',
  EVENT_ID = 'eventid',
  EXCLUDE_NUDITY = 'excludenudity',
  FRAME_RATE = 'framerate',
  GROUP_BY_EVENT = 'groupbyevent',
  IMAGE_SIZE = 'imagesize',
  IMAGE_TECHNIQUE = 'imagetechnique',
  IMAGE_URL = 'imageurl',
  ISTOCK_COLLECTION = 'istockcollection',
  ISTOCK_EXCLUSIVE = 'istockexclusive',
  LICENSE = 'license',
  MORE_LIKE_THIS = 'morelikethis',
  PARENT_EVENT_ID = 'parenteventid',
  PEOPLE = 'people',
  PREMIUM_ACCESS = 'premiumaccess',
  RECENTLY_VIEWED = 'recentlyviewed',
  RELEASE_STATUS = 'releasestatus',
  SEARCH_BY_IMAGE = 'searchbyimage',
  SPECIAL_COLLECTION_ID = 'specialcollectionid',
  STACK_ID = 'stackid',
  SUB_PREVIEW = 'subPreview',
  SUB_PREVIEW_SKU = 'subPreviewSku',
  SUPPRESS_FAMILY_CORRECTION = 'suppressfamilycorrection',
  SUPPRESS_PHRASE_CORRECTION = 'suppressphrasecorrection',
  VIDEO_DURATION_MAX = 'videodurationmax',
  VIDEO_DURATION_MIN = 'videodurationmin',
  VIEWPOINT = 'viewpoint',
}

/**
 * The enum and the schema, pinned together.
 *
 * `MissingFromEnum` lists the names the schema declares and the enum does not;
 * `NotInSchema` the reverse. `Empty` constrains its argument to `never`, so
 * either list having a member fails the build on the alias below. The compiler
 * message says only that the constraint is unmet — hover `MissingFromEnum` or
 * `NotInSchema` to see which names they hold.
 *
 * A build-time check rather than a test because the cost of drift is a name
 * that compiles everywhere and matches nothing. Exported so neither alias
 * reads as dead code — nothing is expected to use them.
 */
type Empty<T extends never> = T;

type MissingFromEnum = Exclude<
  SearchParameterName,
  `${SearchParameterNameEnum}`
>;
type NotInSchema = Exclude<`${SearchParameterNameEnum}`, SearchParameterName>;

export type EveryNameHasAMember = Empty<MissingFromEnum>;
export type EveryMemberHasARow = Empty<NotInSchema>;
